import React, { useState } from 'react';
import {
  Card, Form, Input, Button, Space, message, Descriptions, Tag, Divider,
  Alert, Spin, Table
} from 'antd';
import {
  KeyOutlined, GlobalOutlined, TeamOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ExclamationCircleOutlined, ReloadOutlined
} from '@ant-design/icons';
import { apiClient } from '@/services/api';
import dayjs from 'dayjs';

interface AgentInfo {
  id: string;
  name: string;
  code: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  company?: string;
  status: string;
  maxDomains: number;
  expiresAt?: string;
  domainCount: number;
  domainLicenses: DomainLicenseInfo[];
}

interface DomainLicenseInfo {
  id: string;
  domain: string;
  subdomain?: string;
  fullDomain: string;
  status: string;
  licenseKey: string;
  expiresAt?: string;
  activatedAt?: string;
  lastVerifiedAt?: string;
}

const AgentQueryPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [agentInfo, setAgentInfo] = useState<AgentInfo | null>(null);
  const [queryType, setQueryType] = useState<'agent' | 'domain'>('agent');

  // 查询代理商信息
  const handleQuery = async (values: any) => {
    setLoading(true);
    setAgentInfo(null);
    try {
      const endpoint = queryType === 'agent' ? '/public/agent/query' : '/public/agent/domain-query';
      const requestData = queryType === 'agent' 
        ? { licenseKey: values.licenseKey }
        : { 
            ...(values.licenseKey && { licenseKey: values.licenseKey }),
            ...(values.domain && { domain: values.domain })
          };
      const res = await apiClient.post(endpoint, requestData);
      
      if (res?.success && res.data) {
        if (queryType === 'agent') {
          setAgentInfo(res.data as AgentInfo);
          message.success('查询成功');
        } else {
          // 域名查询：后端可能返回单条对象或数组
          const payload = res.data as any;
          const items = Array.isArray(payload) ? payload : [payload];

          if (items.length === 0) {
            message.warning('未查询到域名授权');
            return;
          }

          // 取第一条的代理商信息用于头部展示
          const first = items[0];
          setAgentInfo({
            id: first.id,
            name: first.agent?.name || '未知代理商',
            code: first.agent?.code || '-',
            status: first.status,
            maxDomains: first.agent?.maxDomains ?? items.length,
            expiresAt: first.agent?.expiresAt ?? first.expiresAt,
            domainCount: items.length,
            domainLicenses: items.map((d: any) => ({
              id: d.id,
              domain: d.domain,
              subdomain: d.subdomain,
              fullDomain: d.fullDomain,
              status: d.status,
              licenseKey: d.licenseKey,
              expiresAt: d.expiresAt,
              activatedAt: d.activatedAt,
              lastVerifiedAt: d.lastVerifiedAt
            }))
          });
          message.success('查询成功');
        }
      } else {
        message.error(res?.message || '查询失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || error.message || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  // 复制密钥
  const copyToClipboard = (text: string, label: string = '密钥') => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label}已复制到剪贴板`);
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success(`${label}已复制到剪贴板`);
      } catch (err) {
        message.error('复制失败，请手动复制');
      }
      document.body.removeChild(textArea);
    });
  };

  const domainColumns = [
    {
      title: '完整域名',
      dataIndex: 'fullDomain',
      key: 'fullDomain',
      render: (text: string) => (
        <Space>
          <GlobalOutlined />
          <strong>{text}</strong>
        </Space>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          ACTIVE: 'green',
          SUSPENDED: 'orange',
          EXPIRED: 'red'
        };
        const labels: Record<string, string> = {
          ACTIVE: '激活',
          SUSPENDED: '暂停',
          EXPIRED: '过期'
        };
        return <Tag color={colors[status]}>{labels[status] || status}</Tag>;
      }
    },
    {
      title: '授权密钥',
      dataIndex: 'licenseKey',
      key: 'licenseKey',
      render: (text: string) => (
        <Space>
          <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
            {text}
          </code>
          <Button
            type="link"
            size="small"
            icon={<KeyOutlined />}
            onClick={() => copyToClipboard(text, '域名授权密钥')}
            title="复制密钥"
          />
        </Space>
      )
    },
    {
      title: '到期时间',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : <Tag color="green">永久</Tag>
    },
    {
      title: '最后验证',
      dataIndex: 'lastVerifiedAt',
      key: 'lastVerifiedAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-2 md:py-8">
      <div className="unified-container max-w-4xl">
        <Card className="unified-card" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div className="text-center" style={{ marginBottom: 'var(--spacing-lg)' }}>
            <TeamOutlined style={{
              fontSize: 'var(--font-h1-desktop)',
              marginBottom: 'var(--spacing-lg)'
            }} className="text-blue-600" />
            <h1 className="unified-title-h1 font-bold text-gray-800" style={{ marginBottom: 'var(--spacing-sm)' }}>代理商授权查询</h1>
            <p className="text-gray-600">通过授权密钥查询代理商和域名授权信息</p>
          </div>

          <Alert
            message="提示"
            description="请输入您的代理商授权密钥或域名授权密钥进行查询。密钥可在管理后台获取。"
            type="info"
            showIcon
            style={{ marginBottom: 'var(--spacing-lg)' }}
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleQuery}
          >
            <Form.Item label="查询类型">
              <Space>
                <Button
                  type={queryType === 'agent' ? 'primary' : 'default'}
                  onClick={() => setQueryType('agent')}
                >
                  代理商查询
                </Button>
                <Button
                  type={queryType === 'domain' ? 'primary' : 'default'}
                  onClick={() => setQueryType('domain')}
                >
                  域名查询
                </Button>
              </Space>
            </Form.Item>

            {queryType === 'agent' ? (
              <Form.Item
                name="licenseKey"
                label="代理商授权密钥"
                rules={[{ required: true, message: '请输入授权密钥' }]}
                help="格式：AGENT-xxx-xxx"
              >
                <Input
                  prefix={<KeyOutlined />}
                  placeholder="AGENT-xxx-xxx"
                  size="large"
                />
              </Form.Item>
            ) : (
              <>
                <Form.Item
                  name="licenseKey"
                  label="域名授权密钥（可选）"
                  help="格式：DOMAIN-xxx-xxx，或直接输入域名查询"
                >
                  <Input
                    prefix={<KeyOutlined />}
                    placeholder="DOMAIN-xxx-xxx（可选）"
                    size="large"
                  />
                </Form.Item>
                <Form.Item
                  name="domain"
                  label="域名（可选）"
                  help="输入域名进行查询，例如：api.duo123.net 或 duo123.net"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value && !form.getFieldValue('licenseKey')) {
                          return Promise.reject(new Error('请输入域名授权密钥或域名'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Input
                    prefix={<GlobalOutlined />}
                    placeholder="api.duo123.net 或 duo123.net"
                    size="large"
                  />
                </Form.Item>
              </>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
                icon={<ReloadOutlined />}
              >
                查询
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {loading && (
          <Card className="text-center py-12">
            <Spin size="large" />
            <div className="mt-4 text-gray-600">正在查询...</div>
          </Card>
        )}

        {agentInfo && !loading && (
          <Card title={
            <Space>
              <TeamOutlined />
              <span>代理商信息</span>
            </Space>
          }>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="代理商名称">
                <strong>{agentInfo.name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="代理商代码">
                <Tag color="blue">{agentInfo.code}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={agentInfo.status === 'ACTIVE' ? 'green' : 'red'}>
                  {agentInfo.status === 'ACTIVE' ? '激活' : agentInfo.status === 'SUSPENDED' ? '暂停' : '过期'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="最大域名数量">
                {agentInfo.maxDomains}
              </Descriptions.Item>
              <Descriptions.Item label="已授权域名">
                {agentInfo.domainCount} / {agentInfo.maxDomains}
              </Descriptions.Item>
              <Descriptions.Item label="授权到期时间">
                {agentInfo.expiresAt ? dayjs(agentInfo.expiresAt).format('YYYY-MM-DD') : <Tag color="green">永久</Tag>}
              </Descriptions.Item>
              {agentInfo.contactName && (
                <Descriptions.Item label="联系人">
                  {agentInfo.contactName}
                </Descriptions.Item>
              )}
              {agentInfo.contactPhone && (
                <Descriptions.Item label="联系电话">
                  {agentInfo.contactPhone}
                </Descriptions.Item>
              )}
              {agentInfo.contactEmail && (
                <Descriptions.Item label="联系邮箱">
                  {agentInfo.contactEmail}
                </Descriptions.Item>
              )}
              {agentInfo.company && (
                <Descriptions.Item label="公司名称" span={2}>
                  {agentInfo.company}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider>域名授权列表</Divider>

            {agentInfo.domainLicenses.length > 0 ? (
              <Table
                columns={domainColumns}
                dataSource={agentInfo.domainLicenses}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ) : (
              <div className="text-center py-8 text-gray-400">
                暂无域名授权
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default AgentQueryPage;

